import ImportExportOutlinedIcon from '@mui/icons-material/ImportExportOutlined';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import SettingsRemoteOutlinedIcon from '@mui/icons-material/SettingsRemoteOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import LaptopChromebookOutlinedIcon from '@mui/icons-material/LaptopChromebookOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import type { SvgIconComponent } from '@mui/icons-material';

export type Integration = {
  slug: string;
  name: string;
  category: string;
  icon: SvgIconComponent;
  summary: string;
  details: string[];
  /** Honest one-line status note — never claims more depth than features.ts already establishes. */
  statusNote: string;
};

/**
 * Every integration here is one already named in features.ts (endpoint-management-rmm) and
 * Consolidation.tsx — this file doesn't introduce new capability claims, it gives each one its
 * own page. Depth is kept to what's already established site-wide ("one console over X") rather
 * than inventing per-vendor sync specifics that aren't evidenced.
 */
export const integrations: Integration[] = [
  {
    slug: 'syncro',
    name: 'Syncro',
    category: 'PSA migration',
    icon: ImportExportOutlinedIcon,
    summary:
      'Moving off Syncro doesn\'t have to mean a hard cutover date. Nexus pulls your existing Syncro tickets in on a schedule so both systems can run in parallel until you\'re ready to switch.',
    details: [
      'Connect your Syncro subdomain and API key, then run a sync on demand or on an automatic interval',
      'Syncro tickets are matched and upserted into Nexus — new tickets get created, existing ones get updated, nothing is duplicated on repeat syncs',
      'Runs alongside your live Syncro account during a migration window, instead of forcing a single flag-day cutover',
      'The API key is replace-only and never read back once saved — you can rotate it without us (or anyone reading the settings page) ever seeing the current value',
    ],
    statusNote: 'A real, working interim sync built specifically for MSPs migrating off Syncro — not a roadmap item.',
  },
  {
    slug: 'network-monitoring',
    name: 'FortiGate, Omada, Meraki & UniFi',
    category: 'Network monitoring',
    icon: LanOutlinedIcon,
    summary:
      'Whatever network gear you\'ve already standardized on across client sites, Nexus folds its monitoring into the same console as everything else — no separate vendor portal per brand.',
    details: [
      'FortiGate, TP-Link Omada, Meraki, and UniFi network monitoring inside the same endpoint management console as your Windows/Linux fleet',
      'Network status and firewall posture surface next to device health, not in a fourth tab you have to remember to check',
      'Findings feed the same ticket and alert pipeline as every other monitoring source — an issue on a client\'s firewall becomes a ticket with full context, not a notification in a vendor app',
      'Part of the same outbound-only monitoring architecture as the rest of the platform — no additional inbound access requested at the client site to support it',
    ],
    statusNote: 'Live as part of the endpoint management & RMM module, which runs in our own practice today.',
  },
  {
    slug: 'jamf',
    name: 'Jamf',
    category: 'Apple MDM',
    icon: PhoneIphoneOutlinedIcon,
    summary:
      'For clients on Apple hardware, Jamf-managed devices show up in the same unified console as everything else Nexus manages — one pane of glass across a mixed fleet.',
    details: [
      'Jamf-managed macOS and iOS devices visible and manageable from the same console as your native Windows/Linux endpoints',
      'No separate Jamf portal login required for day-to-day ticket and device work — the device record a tech opens from a ticket is the same regardless of platform',
      'Sits alongside Nexus\'s own native agent rather than replacing Jamf outright — you keep the MDM you\'ve already deployed and standardized on',
    ],
    statusNote: 'Part of the endpoint management & RMM module\'s multi-MDM console, live in our own practice.',
  },
  {
    slug: 'intune',
    name: 'Microsoft Intune',
    category: 'Microsoft 365 MDM',
    icon: DevicesOtherOutlinedIcon,
    summary:
      'If a client is already on Microsoft 365 and managing devices through Intune, that fleet joins the same console as everything else instead of requiring a second admin center to check daily.',
    details: [
      'Intune-managed Windows and mobile devices visible in the same device inventory as every other endpoint Nexus tracks',
      'Ties into the same ticket-linked device history as native and Jamf-managed devices — one record per device, not one per management tool',
      'Complements the Microsoft 365 backup module already built on the Graph API, so a client\'s full Microsoft footprint sits in one platform',
    ],
    statusNote: 'Part of the endpoint management & RMM module\'s multi-MDM console, live in our own practice.',
  },
  {
    slug: 'ninjaone',
    name: 'NinjaOne',
    category: 'RMM',
    icon: SettingsRemoteOutlinedIcon,
    summary:
      'If a client site is already running NinjaOne and a rip-and-replace isn\'t realistic yet, its device data still surfaces in the same console as the rest of your fleet.',
    details: [
      'NinjaOne-managed endpoints visible from the same unified device view as Nexus-native and other MDM-managed devices',
      'A practical bridge during a migration — you\'re not stuck choosing between "see everything in one place" and "switch every site over on day one"',
      'The same ticket-and-device linkage applies regardless of which agent is actually installed on a given machine',
    ],
    statusNote: 'Part of the endpoint management & RMM module\'s multi-MDM/RMM console, live in our own practice.',
  },
  {
    slug: 'action1',
    name: 'Action1',
    category: 'Patch management',
    icon: SystemUpdateAltOutlinedIcon,
    summary:
      'For sites already standardized on Action1 for patching, that patch status rolls into the same view as Nexus\'s own ring-rollout patch engine — one place to see what\'s current and what\'s behind.',
    details: [
      'Action1-managed patch status visible alongside Nexus\'s native patch deployment (ring rollout, maintenance windows, auto-halt) in one console',
      'Useful during a phased migration onto Nexus\'s own patch engine, or for sites where Action1 stays the tool of record',
      'Patch gaps surface as findings the same way a vulnerability scan finding does — an owner and an SLA, not a report nobody reads',
    ],
    statusNote: 'Part of the endpoint management & RMM module, live in our own practice.',
  },
  {
    slug: 'google-workspace',
    name: 'Google Workspace',
    category: 'Chromebook & Google directory sync',
    icon: LaptopChromebookOutlinedIcon,
    summary:
      'For Chromebook-heavy fleets, Nexus pulls your managed ChromeOS devices and user directory straight out of Google Workspace instead of asking you to check a second admin console.',
    details: [
      'Connects via a Google service account with domain-wide delegation — one-time setup, no per-user OAuth prompts or browser extension to deploy',
      'Syncs every managed ChromeOS device (serial, model, org unit, last activity) from the Admin SDK Directory API into the same device inventory as your Windows/Linux/Apple fleet',
      'Also syncs the Workspace user directory, so staff and student accounts tie to the same records tickets and device history already use',
      'Read-only against Google\'s Directory API — sync and reporting only, no remote wipe or policy push; that stays in the Google Admin console',
    ],
    statusNote: 'Live — the Chromebook and directory sync we run against our own K-12 school clients today.',
  },
  {
    slug: 'backup-vendors',
    name: 'Datto, Slide, Acronis, Veeam & MSP360',
    category: 'Backup & BCDR monitoring',
    icon: CloudSyncOutlinedIcon,
    summary:
      'If a client site already runs a dedicated BCDR appliance or backup vendor, its job status rolls into the same view as Nexus\'s own Microsoft 365 backup instead of a login you have to remember to check.',
    details: [
      'Polls each configured vendor on a schedule and reports the same shape back — device/job name, last run, last status, size — regardless of which vendor is behind it',
      'One vendor going down or returning bad data doesn\'t block the others — a failed poll surfaces as its own finding instead of hiding the rest of the fleet\'s backup status',
      'Read-only: Nexus reports on backup job health, it never triggers or deletes a backup on your behalf',
      'Datto, Slide, and MSP360 connect to a fixed vendor endpoint; Acronis and Veeam take an admin-typed console URL (self-hosted/regional deployments), routed through the same SSRF-guarded outbound path as every other admin-configured integration',
    ],
    statusNote: 'Live — read-only status polling for all five vendors, running in our own practice today.',
  },
  {
    slug: 'device-warranty',
    name: 'Dell & Lenovo warranty lookup',
    category: 'Device warranty',
    icon: VerifiedOutlinedIcon,
    summary:
      'Instead of a tech pasting a serial number into a vendor site to check coverage, Nexus looks up warranty status and basic hardware spec automatically from the device record.',
    details: [
      'Dell (TechDirect OAuth2 + Asset Entitlements API) and Lenovo (Support API) resolve automatically from the device\'s serial/service tag — no manual lookup step',
      'One call returns both warranty end date and a basic product-line description, attached directly to the device record',
      'Upcoming and lapsed warranties surface the same way an end-of-life date does elsewhere in asset management — proactively, not only when someone thinks to check',
      'HP is a placeholder pending partner-API access, and Apple coverage requires Apple Authorized Service Provider access we don\'t have — both fall back to honest manual entry rather than a fake automated result',
    ],
    statusNote: 'Dell and Lenovo are live in our own practice; HP and Apple stay manual entry by design, not a gap we\'re hiding.',
  },
  {
    slug: 'microsoft-365',
    name: 'Microsoft 365',
    category: 'Microsoft 365 admin, sync & backup',
    icon: CloudOutlinedIcon,
    summary:
      'Instead of bouncing between the Microsoft 365 admin center, Intune, and a separate backup tool, Nexus talks to Microsoft Graph directly and puts user management, device sync, and backup in the same console as everything else.',
    details: [
      'User and group lifecycle via Microsoft Graph — create, disable, enable, reset password, and delete users; create/delete groups and manage membership — gated behind an explicit "allow changes" switch and fully audited, with typed-UPN confirmation required before any deletion',
      'Intune-managed device sync into the same device inventory as the rest of the fleet, with an automatic Entra ID /devices fallback when a tenant has no Intune license',
      'An MFA-registration security scan and a subscribed-SKU license report, both pulled straight from Graph on demand or on an automatic schedule',
      'Microsoft 365 backup — Exchange Online, OneDrive, and SharePoint written to your own S3-compatible bucket, SHA-256 verified, with per-item restore and scheduled retention pruning',
    ],
    statusNote: 'Live — real Microsoft Graph calls for admin, device sync, and backup, with directory writes off by default behind an explicit admin switch.',
  },
  {
    slug: 'quickbooks',
    name: 'QuickBooks Online',
    category: 'Accounting sync',
    icon: ReceiptLongOutlinedIcon,
    summary:
      'Invoices you generate in Nexus don\'t have to be re-typed into your accounting software — they push to QuickBooks Online as real invoices, and paid ones sync back automatically.',
    details: [
      'Push any Suite invoice to QuickBooks Online as a real invoice via the QBO API, with the resulting QuickBooks doc number stored on the invoice record',
      'When a recurring Stripe-billed invoice is marked paid through the signature-verified webhook, it\'s created and synced into QuickBooks automatically — no manual re-entry',
      'A reconcile step pulls paid status back out of QuickBooks (matched by invoice number/doc id) and marks the matching Suite invoice paid',
      'Until a QuickBooks realm ID and access token are connected, push and reconcile calls return a clearly labeled simulated response instead of a broken button',
    ],
    statusNote: 'Live once a QuickBooks Online connection is configured; unconfigured tenants get an honestly-labeled simulated response so the flow can be tested before go-live.',
  },
  {
    slug: 'stripe',
    name: 'Stripe',
    category: 'Payments',
    icon: PaymentOutlinedIcon,
    summary:
      'Getting paid shouldn\'t mean chasing a check. Nexus can put any invoice in front of a client as a Stripe-hosted payment page, or put a recurring contract on autopilot with ACH or card auto-charge.',
    details: [
      'Push any Suite invoice to Stripe as a customer-payable hosted invoice with its own hosted payment URL',
      'Map a recurring contract to a Stripe Subscription so Stripe auto-charges on schedule (ACH or card) — the payment method is collected through a Stripe-hosted Setup Session, so card and bank details never touch the app',
      'A signature-verified Stripe webhook records paid invoices back into Suite (with a dedupe window against duplicate/replayed events) and reflects subscription cancellations and failed-payment notices',
      'The app only configures and authorizes billing — Stripe is what actually moves the money; there is no charge or capture logic in this codebase',
    ],
    statusNote: 'Live once a Stripe secret key is configured — real Invoice/Subscription/webhook calls; unconfigured tenants see clearly marked simulated IDs instead of a broken flow.',
  },
  {
    slug: 'twilio',
    name: 'Twilio',
    category: 'SMS alerting',
    icon: SmsOutlinedIcon,
    summary:
      'A critical alert that only lives in a dashboard is an alert nobody sees until Monday. Nexus can text your on-call number directly through Twilio the moment something crosses your severity threshold.',
    details: [
      'Sends SMS via the Twilio Messages API for alerts that meet a configurable minimum severity, alongside Slack/Teams/Discord webhook posts on the same alert',
      'Supports multiple recipient numbers per tenant, with each send authenticated using the account SID and auth token configured in ChatOps settings',
      'A one-off "send test message" action lets an admin confirm Twilio delivery is actually wired up before relying on it for a real page',
      'Best-effort by design — a Twilio failure never blocks alert creation or delivery to the other configured channels',
    ],
    statusNote: 'Live — a real Twilio Messages API call once SID, auth token, from, and to numbers are configured; sends are best-effort and never block alert creation.',
  },
  {
    slug: 'slack',
    name: 'Slack',
    category: 'ChatOps alerting',
    icon: ForumOutlinedIcon,
    summary:
      'Instead of a tech having to remember to check the alerts tab, alerts that meet your severity bar show up in the Slack channel your team already has open.',
    details: [
      'Posts to a Slack incoming webhook for alerts that meet a configurable minimum severity, formatted with severity, category, title, host, and detail',
      'The same alert fans out to Teams and Discord webhooks and Twilio SMS at once, so one alert reaches every channel a tenant has configured, not just Slack',
      'The webhook URL is admin-typed and routed through the platform\'s SSRF-guarded outbound path, the same as every other admin-configured URL',
      'A test-send action confirms the webhook is wired correctly before a tenant depends on it for real alerts',
    ],
    statusNote: 'Live — a real POST to your Slack incoming webhook once configured in ChatOps settings; best-effort, so a Slack outage never blocks the alert or the other channels.',
  },
  {
    slug: 'breach-monitoring',
    name: 'Breach monitoring (Have I Been Pwned)',
    category: 'Dark-web / breach monitoring',
    icon: GppMaybeOutlinedIcon,
    summary:
      'Instead of finding out about a credential breach when someone else exploits it, Nexus checks your domain against Have I Been Pwned and turns a hit into a tracked security finding.',
    details: [
      'Checks a verified domain against the HIBP breached-domain API (requires a paid HIBP API key and a domain verified in that HIBP account)',
      'A new account-in-breach hit creates a deduplicated finding and raises a high-severity Security alert with a clear remediation instruction — it becomes a tracked task, not a report nobody reads',
      'Runs on a recurring schedule (daily by default, per tenant) in addition to an on-demand scan',
      'Also includes a free Pwned Passwords k-anonymity check — only a SHA-1 prefix is ever sent, never the plaintext password — usable with no API key at all',
    ],
    statusNote: 'Live — real HIBP API calls once a key and verified domain are configured, feeding the same alert pipeline as every other finding; the free password-check tool needs no configuration.',
  },
];

export function getIntegrationBySlug(slug: string | undefined): Integration | undefined {
  return integrations.find((i) => i.slug === slug);
}
